// Test mode for local development
// used in getCertificationsWithCourses()
// anything > 0 will return steps and progress set for the current user on courses
let testMode = 0;

// Setter for test mode
export function setTestMode(value: number) {
  testMode = value;
}

import { get as apiGet, del as apiDelete, getActiveProfileId } from './apiService';
import { Course } from '../types/course';
import { Certification } from '../types/certification';
import { Quiz } from '../types/quiz';
import { Game } from '../types/lessonGame';
import { UserCertification } from '../types/userCertification';

// Get course by slug
export async function getCourseBySlug(slug: string): Promise<Course> {
  return apiGet<Course>(`/course/slug/${slug}`);
}

// Get certifications
export async function getCertifications(serviceKey?: string): Promise<Certification[]> {
  return apiGet<Certification[]>('/certification', serviceKey);
}

// Get certifications with courses
export async function getCertificationsWithCourses(): Promise<Certification[]> {
    let endpoint = '/certification/courses?expand=userCourse';

    const isLocal = !import.meta.env.VITE_ENV?.startsWith('prod');
    if (isLocal && testMode) endpoint += `&test=${testMode}`;

    return apiGet<Certification[]>(endpoint);
}

// Get user's certifications
export async function getUserCertifications(profileId: string): Promise<UserCertification[]> {
  let endpoint = `/certifications/${profileId}`;

  const isLocal = !import.meta.env.VITE_ENV?.startsWith('prod');
  if (isLocal && testMode) endpoint += `?test=${testMode}`;

  return apiGet<UserCertification[]>(endpoint);
}

// Get quiz by course ID
export async function getQuiz(courseId: string): Promise<Quiz> {
  const quiz = await apiGet<Quiz>(`/course/${courseId}/quiz?expand=quizQuestions`);
  // Sort quiz questions by number (1...N)
  if (quiz.quizQuestions) {
    quiz.quizQuestions.sort((a, b) => a.number - b.number);
  }
  return quiz;
}

// Get game by course ID
export async function getGame(courseId: string): Promise<Game> {
  return apiGet<Game>(`/course/${courseId}/game?expand=scenarios,finalResults,scoreMeter,prospectProfile,timelineStages`);
}

// Delete all user certifications
export async function deleteUserCertifications(): Promise<void> {
  const profileId = getActiveProfileId();
  if (!profileId) {
    throw new Error('No active profile ID');
  }
  return apiDelete<void>(`/certifications/${profileId}`);
}

// Delete user course progress for a specific certification and course
export async function deleteUserCourse(certificationId: string, courseId: string): Promise<void> {
  const profileId = getActiveProfileId();
  if (!profileId) {
    throw new Error('No active profile ID');
  }
  return apiDelete<void>(`/certification/${certificationId}/course/${courseId}/user/${profileId}`);
}

export const courseService = {
  getCourseBySlug,
  getCertifications,
  getCertificationsWithCourses,
  getUserCertifications,
  getQuiz,
  getGame,
  deleteUserCertifications,
  deleteUserCourse,
};
